import React from "react";
import {
  Bell,
  CalendarDays,
  CheckCircle2,
  Heart,
  MapPin,
  MessageCircle,
  Share2,
  UsersRound,
} from "lucide-react";

import { getMobileNotificationText } from "./friendNotificationUtils";

function getMobileNotificationIcon(notification) {
  switch (notification?.type) {
    case "friend_accepted":
    case "group_invite":
    case "group_join_request":
    case "group_join_request_approved":
    case "group_join_request_rejected":
      return UsersRound;
    case "like":
    case "comment_like":
      return Heart;
    case "comment":
    case "comment_reply":
    case "mention_post":
    case "mention_comment":
    case "mention_reply":
      return MessageCircle;
    case "share":
      return Share2;
    case "trip_shared":
      return CalendarDays;
    case "location_shared":
      return MapPin;
    case "group_post_pending":
    case "group_post_approved":
    case "group_post_rejected":
      return CheckCircle2;
    default:
      return Bell;
  }
}

export default function FriendNotificationList({
  mobile = false,
  notifications,
  notificationCount = notifications.length,
  hasHiddenNotifications,
  usersMap,
  getAvatarSrc,
  renderTimestamp,
  onOpenPostNotification,
  onAcceptSharedTrip,
  onDeclineSharedTrip,
  onAcceptGroupInvite,
  onDeclineGroupInvite,
  onApproveGroupJoinRequest,
  onRejectGroupJoinRequest,
  onApprovePendingGroupPost,
  onRejectPendingGroupPost,
  onOpenSharedLocation,
  onShowAll,
  t,
}) {
  const renderUnreadStatus = (notification) =>
    notification.read ? null : (
      <span className="notif-sr-only">{t("notifications_unread_status")}</span>
    );

  const renderMobileNotificationActions = (notification) => {
    if (notification.type === "trip_shared") {
      return (
        <div className="notif-mobile-row__actions">
          <button
            className="accept-btn"
            onClick={() => onAcceptSharedTrip(notification.id)}
          >
            {t("accept")}
          </button>
          <button
            className="decline-btn"
            onClick={() => onDeclineSharedTrip(notification.id)}
          >
            {t("decline")}
          </button>
        </div>
      );
    }

    if (notification.type === "group_invite") {
      return (
        <div className="notif-mobile-row__actions">
          <button
            className="accept-btn"
            onClick={() => onAcceptGroupInvite(notification.id)}
          >
            {t("accept")}
          </button>
          <button
            className="decline-btn"
            onClick={() => onDeclineGroupInvite(notification.id)}
          >
            {t("decline")}
          </button>
        </div>
      );
    }

    if (notification.type === "group_join_request") {
      return (
        <div className="notif-mobile-row__actions">
          <button
            className="accept-btn"
            onClick={() => onApproveGroupJoinRequest(notification.id)}
          >
            {t("approve", { defaultValue: "Approve" })}
          </button>
          <button
            className="decline-btn"
            onClick={() => onRejectGroupJoinRequest(notification.id)}
          >
            {t("reject", { defaultValue: "Reject" })}
          </button>
        </div>
      );
    }

    if (notification.type === "group_post_pending") {
      return (
        <div className="notif-mobile-row__actions">
          <button
            className="accept-btn"
            onClick={() =>
              onApprovePendingGroupPost(
                notification.groupId,
                notification.postId
              )
            }
          >
            {t("approve", { defaultValue: "Approve" })}
          </button>
          <button
            className="decline-btn"
            onClick={() =>
              onRejectPendingGroupPost(
                notification.groupId,
                notification.postId
              )
            }
          >
            {t("reject", { defaultValue: "Reject" })}
          </button>
        </div>
      );
    }

    if (notification.type === "location_shared") {
      return (
        <div className="notif-mobile-row__actions">
          <button
            className="accept-btn"
            onClick={() => onOpenSharedLocation(notification)}
          >
            {t("view")}
          </button>
        </div>
      );
    }

    return null;
  };

  const renderMobileNotificationRow = (notification) => {
    const user = usersMap[notification.fromUserId];
    const Icon = getMobileNotificationIcon(notification);
    const isPostActivityNotification =
      notification.type === "like" ||
      notification.type === "comment" ||
      notification.type === "comment_like" ||
      notification.type === "comment_reply" ||
      notification.type === "mention_post" ||
      notification.type === "mention_comment" ||
      notification.type === "mention_reply" ||
      notification.type === "share";
    const helperText = isPostActivityNotification
      ? notification.postCaption || ""
      : notification.type === "location_shared" ||
          notification.type === "trip_shared"
        ? notification.location || notification.species || ""
        : "";

    return (
      <article
        key={notification.id}
        className={`notif-mobile-row${
          notification.read ? "" : " notif-mobile-row--unread"
        }${isPostActivityNotification ? " notif-mobile-row--clickable" : ""}`}
        role={isPostActivityNotification ? "button" : undefined}
        tabIndex={isPostActivityNotification ? 0 : undefined}
        onClick={
          isPostActivityNotification
            ? () => onOpenPostNotification(notification)
            : undefined
        }
        onKeyDown={
          isPostActivityNotification
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onOpenPostNotification(notification);
                }
              }
            : undefined
        }
      >
        {renderUnreadStatus(notification)}
        <img
          className="notif-mobile-row__avatar"
          src={getAvatarSrc(user)}
          alt={t("profile_picture")}
        />
        <div className="notif-mobile-row__body">
          <p>{getMobileNotificationText(notification, user, t)}</p>
          {helperText ? <span>{helperText}</span> : null}
          {renderTimestamp(notification.createdAt)}
          {renderMobileNotificationActions(notification)}
        </div>
        <span className="notif-mobile-row__icon" aria-hidden="true">
          <Icon size={17} strokeWidth={2.25} />
        </span>
      </article>
    );
  };

  if (mobile) {
    return (
      <section className="notif-mobile-card">
        <div className="notif-mobile-card__header">
          <div>
            <p>{t("notifications_latest")}</p>
            <h3>{t("notifications_recent")}</h3>
          </div>
          <span>{notificationCount}</span>
        </div>

        {notifications.length === 0 ? (
          <div className="notif-mobile-empty notif-mobile-empty--compact">
            <span className="notif-mobile-empty__icon" aria-hidden="true">
              <Bell size={25} strokeWidth={2.15} />
            </span>
            <strong>{t("notifications_no_recent")}</strong>
            <p>{t("notifications_no_recent_helper")}</p>
          </div>
        ) : (
          <div className="notif-mobile-list">
            {notifications.map(renderMobileNotificationRow)}
          </div>
        )}

        {hasHiddenNotifications ? (
          <button
            type="button"
            className="notif-mobile-view-all"
            onClick={onShowAll}
          >
            {t("notifications_view_all")}
          </button>
        ) : null}
      </section>
    );
  }

  if (notifications.length === 0) return null;

  return (
    <>
      <h4 style={{ marginTop: 10 }}>{t("notifications")}</h4>

      {notifications.map((notification) => {
        const user = usersMap[notification.fromUserId];

        if (notification.type === "trip_shared") {
          return (
            <div
              key={notification.id}
              className={`notif-item ${
                notification.read ? "" : "notif-unread"
              }`}
            >
              {renderUnreadStatus(notification)}
              <img
                src={getAvatarSrc(user)}
                alt={t("profile_picture")}
              />

              <div className="notif-trip-details">
                <span>
                  <strong>{notification.fromUsername}</strong>{" "}
                  {t("shared_trip_with_you")}
                </span>
                {renderTimestamp(notification.createdAt)}

                <div className="notif-trip-line">
                  🗓{" "}
                  {notification.startDate
                    ? new Date(notification.startDate).toLocaleDateString()
                    : ""}
                  {notification.endDate
                    ? ` → ${new Date(
                        notification.endDate
                      ).toLocaleDateString()}`
                    : ""}
                </div>

                {notification.location && (
                  <div className="notif-trip-line">
                    📍 {notification.location}
                  </div>
                )}

                {notification.species && (
                  <div className="notif-trip-line">
                    🎣 {notification.species}
                  </div>
                )}

                <div className="notif-actions">
                  <button
                    className="accept-btn"
                    onClick={() => onAcceptSharedTrip(notification.id)}
                  >
                    {t("accept")}
                  </button>
                  <button
                    className="decline-btn"
                    onClick={() => onDeclineSharedTrip(notification.id)}
                  >
                    {t("decline")}
                  </button>
                </div>
              </div>
            </div>
          );
        }

        if (notification.type === "group_invite") {
          return (
            <div
              key={notification.id}
              className={`notif-item ${
                notification.read ? "" : "notif-unread"
              }`}
            >
              {renderUnreadStatus(notification)}
              <img
                src={getAvatarSrc(user)}
                alt={t("profile_picture")}
              />

              <div className="notif-trip-details">
                <span>
                  <strong>{notification.fromUsername || user?.username}</strong>{" "}
                  {t("groups_invited_you", {
                    defaultValue: "invited you to join",
                  })}{" "}
                  <strong>{notification.groupName}</strong>
                </span>
                {renderTimestamp(notification.createdAt)}

                <div className="notif-actions">
                  <button
                    className="accept-btn"
                    onClick={() => onAcceptGroupInvite(notification.id)}
                  >
                    {t("accept")}
                  </button>
                  <button
                    className="decline-btn"
                    onClick={() => onDeclineGroupInvite(notification.id)}
                  >
                    {t("decline")}
                  </button>
                </div>
              </div>
            </div>
          );
        }

        if (notification.type === "group_join_request") {
          return (
            <div
              key={notification.id}
              className={`notif-item ${
                notification.read ? "" : "notif-unread"
              }`}
            >
              {renderUnreadStatus(notification)}
              <img
                src={getAvatarSrc(user)}
                alt={t("profile_picture")}
              />

              <div className="notif-trip-details">
                <span>
                  <strong>
                    {notification.requesterUsername ||
                      notification.fromUsername ||
                      user?.username}
                  </strong>{" "}
                  {t("groups_requested_to_join", {
                    defaultValue: "requested to join",
                  })}{" "}
                  <strong>{notification.groupName}</strong>
                </span>
                {renderTimestamp(notification.createdAt)}

                <div className="notif-actions">
                  <button
                    className="accept-btn"
                    onClick={() =>
                      onApproveGroupJoinRequest(notification.id)
                    }
                  >
                    {t("approve", { defaultValue: "Approve" })}
                  </button>
                  <button
                    className="decline-btn"
                    onClick={() => onRejectGroupJoinRequest(notification.id)}
                  >
                    {t("reject", { defaultValue: "Reject" })}
                  </button>
                </div>
              </div>
            </div>
          );
        }

        if (notification.type === "group_post_pending") {
          return (
            <div
              key={notification.id}
              className={`notif-item ${
                notification.read ? "" : "notif-unread"
              }`}
            >
              {renderUnreadStatus(notification)}
              <img
                src={getAvatarSrc(user)}
                alt={t("profile_picture")}
              />

              <div className="notif-trip-details">
                <span>
                  <strong>{notification.fromUsername || user?.username}</strong>{" "}
                  {t("groups_post_pending_notification", {
                    defaultValue: "submitted a post in",
                  })}{" "}
                  <strong>{notification.groupName}</strong>{" "}
                  {t("groups_post_pending_notification_suffix", {
                    defaultValue: "that needs approval.",
                  })}
                </span>
                {renderTimestamp(notification.createdAt)}

                {notification.postCaption ? (
                  <div className="notif-trip-line">
                    {notification.postCaption}
                  </div>
                ) : null}

                <div className="notif-actions">
                  <button
                    className="accept-btn"
                    onClick={() =>
                      onApprovePendingGroupPost(
                        notification.groupId,
                        notification.postId
                      )
                    }
                  >
                    {t("approve", { defaultValue: "Approve" })}
                  </button>
                  <button
                    className="decline-btn"
                    onClick={() =>
                      onRejectPendingGroupPost(
                        notification.groupId,
                        notification.postId
                      )
                    }
                  >
                    {t("reject", { defaultValue: "Reject" })}
                  </button>
                </div>
              </div>
            </div>
          );
        }

        if (notification.type === "location_shared") {
          return (
            <div
              key={notification.id}
              className={`notif-item ${
                notification.read ? "" : "notif-unread"
              }`}
            >
              {renderUnreadStatus(notification)}
              <img src={getAvatarSrc(user)} alt="" />

              <div className="notif-trip-details">
                <span>
                  <strong>{notification.fromUsername}</strong>{" "}
                  {t("shared_location_with_you")}
                </span>
                {renderTimestamp(notification.createdAt)}

                {notification.location && (
                  <div className="notif-trip-line">
                    📍 {notification.location}
                  </div>
                )}

                <button
                  className="accept-btn"
                  onClick={() => onOpenSharedLocation(notification)}
                >
                  {t("view")}
                </button>
              </div>
            </div>
          );
        }

        if (
          notification.type === "follow" ||
          notification.type === "friend_accepted" ||
          notification.type === "like" ||
          notification.type === "comment" ||
          notification.type === "comment_like" ||
          notification.type === "comment_reply" ||
          notification.type === "share" ||
          notification.type === "mention_post" ||
          notification.type === "mention_comment" ||
          notification.type === "mention_reply" ||
          notification.type === "group_join_request_approved" ||
          notification.type === "group_join_request_rejected" ||
          notification.type === "group_post_approved" ||
          notification.type === "group_post_rejected"
        ) {
          const isPostActivityNotification =
            notification.type === "like" ||
            notification.type === "comment" ||
            notification.type === "comment_like" ||
            notification.type === "comment_reply" ||
            notification.type === "mention_post" ||
            notification.type === "mention_comment" ||
            notification.type === "mention_reply" ||
            notification.type === "share";

          return (
            <div
              key={notification.id}
              className={`notif-item ${
                notification.read ? "" : "notif-unread"
              }${isPostActivityNotification ? " notif-clickable" : ""}`}
              role={isPostActivityNotification ? "button" : undefined}
              tabIndex={isPostActivityNotification ? 0 : undefined}
              onClick={
                isPostActivityNotification
                  ? () => onOpenPostNotification(notification)
                  : undefined
              }
              onKeyDown={
                isPostActivityNotification
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onOpenPostNotification(notification);
                      }
                    }
                  : undefined
              }
            >
              {renderUnreadStatus(notification)}
              <img
                src={getAvatarSrc(user)}
                alt={t("profile_picture")}
              />

              <div className="notif-main">
                <span>{getMobileNotificationText(notification, user, t)}</span>
                {renderTimestamp(notification.createdAt)}
              </div>
            </div>
          );
        }

        return (
          <div
            key={notification.id}
            className={`notif-item ${
              notification.read ? "" : "notif-unread"
            }`}
          >
            {renderUnreadStatus(notification)}
            <img src={getAvatarSrc(user)} alt={t("profile_picture")} />
            <div className="notif-main">
              <span>{getMobileNotificationText(notification, user, t)}</span>
              {renderTimestamp(notification.createdAt)}
            </div>
          </div>
        );
      })}

      {hasHiddenNotifications ? (
        <button
          type="button"
          className="notif-expand-btn"
          onClick={onShowAll}
        >
          {t("see_all", { defaultValue: "See all" })}
        </button>
      ) : null}
    </>
  );
}
